import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { canEditModule } from "@/utils/permissions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Building2,
  ArrowLeft,
  UserPlus,
  Download,
  User,
  Users,
  FileText,
  Ban,
  CheckCircle
} from "lucide-react";
import * as XLSX from "xlsx";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";

const ClientCRM = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingClient, setEditingClient] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const isAdmin = user?.role === "admin";
  const isSupervisor = user?.role === "supervisor";
  const canToggleActive = isAdmin || isSupervisor;
  const userCanEdit = canEditModule(user, "clientes_crm");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data);
    } catch (error) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (client) => {
    const action = client.is_active !== false ? "inactivar" : "reactivar";
    if (!window.confirm(`¿Estás seguro de ${action} este cliente?`)) return;
    try {
      const res = await axios.patch(`${API}/clients/${client.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setClients(clients.map(c => 
        c.id === client.id ? { ...c, is_active: res.data.is_active } : c
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este cliente?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cliente eliminado");
      setClients(clients.filter(c => c.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleEdit = (client) => {
    setEditingClient({ ...client });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/clients/${editingClient.id}`, editingClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cliente actualizado");
      setShowEditDialog(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const filteredClients = clients.filter(client => {
    // Filtrar por estado activo/inactivo
    if (showInactive) {
      if (client.is_active !== false) return false;
    } else {
      if (client.is_active === false) return false;
    }
    
    const search = searchTerm.toLowerCase();
    return (
      client.company_name?.toLowerCase().includes(search) ||
      client.rfc?.toLowerCase().includes(search) ||
      client.contact_name?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.folio?.toLowerCase().includes(search)
    );
  });

  const exportToExcel = (type = "current") => {
    if (filteredClients.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    let data;
    
    if (type === "current") {
      // Vista Actual: Solo las columnas mostradas en la tabla
      data = filteredClients.map(client => ({
        "Fecha Registro": client.created_at ? new Date(client.created_at).toLocaleDateString("es-MX") : "",
        "Registrado por": client.created_by_name || "",
        "Folio": client.folio || "",
        "Empresa": client.company_name || "",
        "RFC": client.rfc || "",
        "Contacto": client.contact_name || "",
        "Teléfono": client.phone || "",
        "Email": client.email || ""
      }));
    } else {
      // Vista Completa: Todos los campos del registro
      data = filteredClients.map(client => ({
        "ID": client.id || "",
        "Folio": client.folio || "",
        "Empresa / Razón Social": client.company_name || "",
        "Nombre comercial": client.nombre_comercial || "",
        "RFC": client.rfc || "",
        "País de Origen": client.country || "",
        "Dirección": client.address || "",
        "Nombre del Contacto": client.contact_name || "",
        "Teléfono": client.phone || "",
        "Email": client.email || "",
        "Fuente (Cómo nos conoció)": client.source || "",
        "Registrado por (ID)": client.created_by_id || "",
        "Registrado por (Nombre)": client.created_by_name || "",
        "Fecha de Registro": client.created_at ? new Date(client.created_at).toLocaleString("es-MX") : "",
        "Fecha de Actualización": client.updated_at ? new Date(client.updated_at).toLocaleString("es-MX") : ""
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clientes");
    
    const today = new Date().toISOString().split("T")[0];
    const suffix = type === "full" ? "_completo" : "_vista";
    XLSX.writeFile(workbook, `clientes${suffix}_${today}.xlsx`);
    
    const columnCount = type === "full" ? "todos los campos" : "campos de la vista";
    toast.success(`Excel exportado con ${columnCount}`);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando clientes...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="client-crm-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <ModuleHeaderIcon icon={Users} />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
              <p className="text-slate-500 text-sm">{filteredClients.length} de {clients.length} clientes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={filteredClients.length === 0}>
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Excel
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportToExcel("current")}>
                  <FileText className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span>Vista Actual</span>
                    <span className="text-xs text-slate-500">Solo columnas del panel</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToExcel("full")}>
                  <Download className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span>Vista Completa</span>
                    <span className="text-xs text-slate-500">Todos los campos del registro</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => navigate("/clientes/nuevo")} data-testid="new-client-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Nuevo Cliente
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por folio, empresa, RFC, contacto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-clients"
                />
              </div>
              <label className="flex items-center gap-2 ml-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <span className="text-sm text-slate-600">Mostrar solo inactivos</span>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {filteredClients.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No se encontraron clientes</p>
                <Button variant="link" onClick={() => navigate("/clientes/nuevo")}>
                  Registrar primer cliente
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha Registro</TableHead>
                      <TableHead>Registrado por</TableHead>
                      <TableHead>Folio</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>RFC</TableHead>
                      <TableHead>Contacto</TableHead>
                      <TableHead>Teléfono</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Estatus</TableHead>
                      <TableHead>Bind</TableHead>
                      <TableHead className="w-[80px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map((client) => (
                      <TableRow 
                        key={client.id} 
                        className="cursor-pointer hover:bg-slate-50 [&>td]:py-1.5 [&>td]:whitespace-nowrap"
                        onClick={() => navigate(`/clientes/${client.id}`)}
                      >
                        <TableCell className="text-xs text-slate-500">
                          {formatDate(client.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <User className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="text-xs truncate max-w-[140px]" title={client.created_by_name || ""}>{client.created_by_name || "—"}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{client.folio}</TableCell>
                        <TableCell className="font-medium text-sm max-w-[240px] truncate" title={client.company_name}>{client.company_name}</TableCell>
                        <TableCell className="font-mono text-xs">{client.rfc}</TableCell>
                        <TableCell className="text-sm max-w-[160px] truncate" title={client.contact_name || ""}>{client.contact_name}</TableCell>
                        <TableCell className="text-sm">{client.phone}</TableCell>
                        <TableCell className="text-sm max-w-[220px] truncate" title={client.email || ""}>{client.email}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                            client.is_active !== false 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {client.is_active !== false ? 'Activo' : 'Inactivo'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {client.bind_client_id ? (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700" data-testid="bind-linked-badge">Vinculado</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {userCanEdit && (
                                <DropdownMenuItem onClick={() => handleEdit(client)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {canToggleActive && (
                                <DropdownMenuItem onClick={() => handleToggleActive(client)}>
                                  {client.is_active !== false ? (
                                    <>
                                      <Ban className="w-4 h-4 mr-2 text-orange-500" />
                                      <span className="text-orange-600">Inactivar</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                                      <span className="text-green-600">Reactivar</span>
                                    </>
                                  )}
                                </DropdownMenuItem>
                              )}
                              {isAdmin && (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(client.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              )}
                              {!isAdmin && !isSupervisor && (
                                <DropdownMenuItem disabled>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Solo lectura
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Editar Cliente</DialogTitle>
              <DialogDescription>
                Modifica los datos del cliente. Solo administradores pueden editar.
              </DialogDescription>
            </DialogHeader>
            {editingClient && (
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="space-y-2">
                  <Label>Empresa / Razón Social</Label>
                  <Input
                    value={editingClient.company_name}
                    onChange={(e) => setEditingClient({...editingClient, company_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre comercial del cliente</Label>
                  <Input
                    value={editingClient.nombre_comercial || ""}
                    placeholder="Se muestra en documentos al cliente final"
                    onChange={(e) => setEditingClient({...editingClient, nombre_comercial: e.target.value})}
                    data-testid="edit-nombre-comercial"
                  />
                </div>
                <div className="space-y-2">
                  <Label>RFC</Label>
                  <Input
                    value={editingClient.rfc}
                    onChange={(e) => setEditingClient({...editingClient, rfc: e.target.value.toUpperCase()})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre del Contacto</Label>
                  <Input
                    value={editingClient.contact_name}
                    onChange={(e) => setEditingClient({...editingClient, contact_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={editingClient.phone}
                    onChange={(e) => setEditingClient({...editingClient, phone: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={editingClient.email}
                    onChange={(e) => setEditingClient({...editingClient, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>País</Label>
                  <Input
                    value={editingClient.country || ""}
                    onChange={(e) => setEditingClient({...editingClient, country: e.target.value})}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Dirección</Label>
                  <Input
                    value={editingClient.address || ""}
                    onChange={(e) => setEditingClient({...editingClient, address: e.target.value})}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit}>
                Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ClientCRM;
